import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff } from "lucide-react";
import { api, errMsg } from "@/lib/api";

function b64ToBytes(s) {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const raw = atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export default function PushOptIn() {
  const [state, setState] = useState("checking"); // checking | unsupported | denied | off | on
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const check = async () => {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        setState("unsupported");
        return;
      }
      if (Notification.permission === "denied") {
        setState("denied");
        return;
      }
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        setState(sub ? "on" : "off");
      } catch {
        setState("off");
      }
    };
    check();
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setState("denied");
        toast.error("Permesso notifiche negato dal browser");
        return;
      }
      const reg = await navigator.serviceWorker.register("/sw.js");
      const { data } = await api.get("/push/public-key");
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: b64ToBytes(data.publicKey),
        });
      }
      await api.post("/push/subscription", sub.toJSON());
      setState("on");
      toast.success("Notifiche attive! Ti avviseremo alla pubblicazione dei punteggi.");
    } catch (e) {
      toast.error(errMsg(e, "Attivazione notifiche fallita"));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await api.delete("/push/subscription", { params: { endpoint: sub.endpoint } });
        await sub.unsubscribe();
      }
      setState("off");
      toast("Notifiche disattivate su questo dispositivo");
    } catch (e) {
      toast.error(errMsg(e, "Disattivazione fallita"));
    } finally {
      setBusy(false);
    }
  };

  if (state === "checking") return null;
  if (state === "unsupported") {
    return (
      <p className="text-xs text-gray-500 font-body-sport" data-testid="push-unsupported">
        Questo browser non supporta le notifiche push.
      </p>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <button
        data-testid="push-toggle-button"
        onClick={state === "on" ? disable : enable}
        disabled={busy || state === "denied"}
        className={`flex items-center gap-2 rounded-lg transition-colors px-5 py-2.5 min-h-[44px] font-body-sport font-bold text-xs tracking-widest uppercase ${
          state === "on"
            ? "border border-[#EF4444]/60 text-[#EF4444] hover:bg-[#EF4444]/10"
            : "bg-[#FF1E27] hover:bg-[#CC1118] text-white disabled:opacity-40"
        }`}
      >
        {state === "on" ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
        {busy ? "..." : state === "on" ? "Disattiva Notifiche" : "Attiva Notifiche"}
      </button>
      {state === "on" && (
        <span data-testid="push-active-badge" className="font-body-sport text-[10px] font-bold tracking-widest text-[#22C55E] border border-[#22C55E]/40 bg-[#22C55E]/10 rounded-full px-3 py-1.5 uppercase">
          Attive su questo dispositivo
        </span>
      )}
      {state === "denied" && (
        <span data-testid="push-denied-hint" className="text-[11px] text-[#FFB703] font-body-sport">
          Permesso negato: riattivalo dalle impostazioni del browser (icona lucchetto nella barra indirizzi).
        </span>
      )}
    </div>
  );
}
